import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setFields,
  setSelectedField,
  setPage,
  setPageSize,
  setSearch,
  setSort,
  addField,
  updateField as updateFieldAction,
  removeField,
  clearSelectedField,
  resetClarificationFields,
} from '../../store/slices/clarificationFieldsSlice';
import {
  GET_CLARIFICATION_FIELDS_LIST,
  GET_CLARIFICATION_FIELD,
} from '../../graphql/queries/clarificationFields.queries';
import {
  CREATE_CLARIFICATION_FIELD,
  UPDATE_CLARIFICATION_FIELD,
  DELETE_CLARIFICATION_FIELD,
} from '../../graphql/mutations/clarificationFields.mutations';
import {
  FindClarificationFieldListInput,
  FindClarificationFieldInput,
  CreateClarificationFieldInput,
  UpdateClarificationFieldInput,
  DeleteClarificationFieldInput,
  ClarificationFieldListResponse,
  ClarificationFieldResponse,
  ClarificationFieldMutationResponse,
  DeleteClarificationFieldResponse,
  ClarificationField,
} from '../../types/clarificationFields.types';
import ViewClarificationFieldModal from '../../components/ClarificationFields/ViewClarificationFieldModal';
import CreateEditClarificationFieldModal, {
  ClarificationFieldFormData,
} from '../../components/ClarificationFields/CreateEditClarificationFieldModal';
import ConfirmDeleteClarificationFieldDialog from '../../components/ClarificationFields/ConfirmDeleteClarificationFieldDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './ClarificationFields.css';

const ClarificationFields: React.FC = () => {
  const navigate = useNavigate();
  const { schemaId } = useParams<{ schemaId: string }>();
  const dispatch = useAppDispatch();
  const { fields, loading, error, pagination, search, sort } = useAppSelector(
    (state) => state.clarificationFields
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<ClarificationField | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchFields, { loading: fetchingList }] = useLazyQuery<
    { clarificationFields: ClarificationFieldListResponse },
    { params: FindClarificationFieldListInput }
  >(GET_CLARIFICATION_FIELDS_LIST, { fetchPolicy: 'network-only' });

  const [fetchField] = useLazyQuery<
    { clarificationField: ClarificationFieldResponse },
    { params: FindClarificationFieldInput }
  >(GET_CLARIFICATION_FIELD, { fetchPolicy: 'network-only' });

  const [createFieldMutation] = useMutation<
    { createClarificationField: ClarificationFieldMutationResponse },
    { params: CreateClarificationFieldInput }
  >(CREATE_CLARIFICATION_FIELD);

  const [updateFieldMutation] = useMutation<
    { updateClarificationField: ClarificationFieldMutationResponse },
    { params: UpdateClarificationFieldInput }
  >(UPDATE_CLARIFICATION_FIELD);

  const [deleteFieldMutation] = useMutation<
    { deleteClarificationField: DeleteClarificationFieldResponse },
    { params: DeleteClarificationFieldInput }
  >(DELETE_CLARIFICATION_FIELD);

  useEffect(() => {
    dispatch(resetClarificationFields());
  }, [schemaId]);

  useEffect(() => {
    if (schemaId) {
      loadFields();
    }
  }, [pagination.page, pagination.pageSize, search, sort, schemaId]);

  const loadFields = async () => {
    if (!schemaId) return;

    try {
      dispatch(setLoading(true));
      const { data } = await fetchFields({
        variables: {
          params: {
            clarificationSchemaId: schemaId,
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
            sort: {
              field: sort.field,
              order: sort.order,
            },
          },
        },
      });

      if (data?.clarificationFields?.success && data.clarificationFields.data) {
        dispatch(setFields(data.clarificationFields.data));
      } else {
        dispatch(setError(data?.clarificationFields?.message || 'Error al cargar campos'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleSortChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(setSort({ field: model[0].field, order: model[0].sort || 'desc' }));
    } else {
      dispatch(setSort({ field: 'createdAt', order: 'desc' }));
    }
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchField({ variables: { params: { ids: [id] } } });
      if (data?.clarificationField?.success && data.clarificationField.data && data.clarificationField.data.length > 0) {
        dispatch(setSelectedField(data.clarificationField.data[0]));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.clarificationField?.message || 'Error al cargar campo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchField({ variables: { params: { ids: [id] } } });
      if (data?.clarificationField?.success && data.clarificationField.data && data.clarificationField.data.length > 0) {
        dispatch(setSelectedField(data.clarificationField.data[0]));
        setIsEdit(true);
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.clarificationField?.message || 'Error al cargar campo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedField());
    setIsEdit(false);
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: ClarificationFieldFormData) => {
    if (!schemaId) return;

    try {
      setSaving(true);
      setSaveError(null);

      if (isEdit && formData.id) {
        const { data: responseData } = await updateFieldMutation({
          variables: {
            params: {
              id: formData.id,
              clarificationSchemaId: schemaId,
              field: formData.field,
              label: formData.label,
              priority: formData.priority,
              examples: formData.examples,
              defaultBehavior: formData.defaultBehavior,
              applicableTo: formData.applicableTo,
              dynamicOptions: formData.dynamicOptions,
              stage: formData.stage,
              valueMap: formData.valueMap,
              includeInQuery: formData.includeInQuery,
            },
          },
        });

        if (responseData?.updateClarificationField?.success) {
          if (responseData.updateClarificationField.data) {
            dispatch(updateFieldAction(responseData.updateClarificationField.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedField());
          setSaveError(null);
          setSuccessMessage('Campo de clarificación actualizado exitosamente');
        } else {
          const msg = responseData?.updateClarificationField?.message || 'Error al actualizar campo';
          const errs = responseData?.updateClarificationField?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        const { data: responseData } = await createFieldMutation({
          variables: {
            params: {
              clarificationSchemaId: schemaId,
              field: formData.field,
              label: formData.label,
              priority: formData.priority,
              examples: formData.examples,
              defaultBehavior: formData.defaultBehavior,
              applicableTo: formData.applicableTo,
              dynamicOptions: formData.dynamicOptions,
              stage: formData.stage,
              valueMap: formData.valueMap,
              includeInQuery: formData.includeInQuery,
            },
          },
        });

        if (responseData?.createClarificationField?.success) {
          if (responseData.createClarificationField.data) {
            dispatch(addField(responseData.createClarificationField.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedField());
          setSaveError(null);
          setSuccessMessage('Campo de clarificación creado exitosamente');
        } else {
          const msg = responseData?.createClarificationField?.message || 'Error al crear campo';
          const errs = responseData?.createClarificationField?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (field: ClarificationField) => {
    setFieldToDelete(field);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fieldToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteFieldMutation({
        variables: {
          params: {
            ids: [fieldToDelete.id],
          },
        },
      });

      if (responseData?.deleteClarificationField?.success) {
        dispatch(removeField(fieldToDelete.id));
        setDeleteDialogOpen(false);
        setFieldToDelete(null);
        setDeleteError(null);
        setSuccessMessage('Campo de clarificación eliminado exitosamente');
      } else {
        const msg = responseData?.deleteClarificationField?.message || 'Error al eliminar campo';
        const errs = responseData?.deleteClarificationField?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedField());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedField());
    setSaveError(null);
    setSaving(false);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setFieldToDelete(null);
    setDeleteError(null);
    setDeleting(false);
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const columns: GridColDef<ClarificationField>[] = [
    { field: 'field', headerName: 'Campo', width: 180 },
    { field: 'label', headerName: 'Etiqueta', flex: 1, minWidth: 250 },
    { field: 'priority', headerName: 'Prioridad', width: 130 },
    {
      field: 'examples',
      headerName: 'Ejemplos',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string[]) => value.join(', '),
    },
    { field: 'defaultBehavior', headerName: 'Comportamiento', width: 160 },
    {
      field: 'applicableTo',
      headerName: 'Aplicable A',
      width: 150,
      valueGetter: (value: string[]) => value.join(', '),
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleView(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEdit(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="clarification-fields-container">
        <Paper elevation={2} className="clarification-fields-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Link
              underline="hover"
              sx={{ cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/clarification-schemas')}
            >
              Clarificación
            </Link>
            <Typography color="text.primary">Campos</Typography>
          </Breadcrumbs>

          <Box className="clarification-fields-header">
            <Typography variant="h4" component="h1">
              Campos de Clarificación
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de campos para el esquema de clarificación
            </Typography>
          </Box>

          <Box className="clarification-fields-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar campo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button variant="contained" startIcon={<Search />} onClick={handleSearch} sx={{ ml: 1 }}>
                Buscar
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' },
              }}
            >
              Agregar Campo
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={fields}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              sortingMode="server"
              onSortModelChange={handleSortChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            />
          </Box>
        </Paper>

        <ViewClarificationFieldModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditClarificationFieldModal
          open={createEditModalOpen}
          isEdit={isEdit}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteClarificationFieldDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteConfirm}
          fieldName={fieldToDelete?.field || ''}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default ClarificationFields;
